import React, { useMemo, useState } from 'react';
import {
  useRolloverPreviewMutation,
  useRolloverApplyMutation,
  useRolloverUndoMutation,
} from '../../store/api/localApi';
import { getErrorMessage } from '../../utils/errors';
import RolloverClassCard from './RolloverClassCard';
import type {
  RolloverClassPreview,
  RolloverPerClassInput,
  RolloverApplyResult,
  UndoBlocker,
} from '../../types/rollover';

/**
 * The September rollover wizard (spec §5). A guided, admin-only, per-location
 * operation that carries every class's current term into the next school year.
 *
 * Five steps:
 *   1. PREVIEW  — a single "next school year" range defaults every class's next
 *      term; dates are editable per class.
 *   2. CONTINUE/LEAVE — collapsible per-class cards, each with the continuing
 *      count, DOB age-out hints, and editable dates. Everyone continues by
 *      default; the admin explicitly ticks who is NOT returning. Age-out-hinted
 *      children are visually suggested but NEVER pre-selected — leaving
 *      requires a deliberate tick.
 *   3. CONFIRM  — a per-class table (dates, continuing/leaving counts, status,
 *      and an over-capacity pill where the class has capacity conflicts) plus
 *      headline totals, then Apply.
 *   4. RESULT   — created terms, enrolled count, and — prominently — the
 *      children skipped over capacity, so nothing is silently dropped.
 *   5. UNDO     — available on the result. A 409 lists both blocker kinds.
 */

type Step = 'PREVIEW' | 'LEAVE' | 'CONFIRM' | 'RESULT';

/** Default the next school year to roughly the coming academic year. */
function defaultRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const year = now.getUTCFullYear();
  // Sept 1 → Jun 30 of the following calendar year, a sensible starting guess
  // the admin overrides. From September onward the current school year is
  // already running, so the next rollover targets NEXT September; before then,
  // this September. (getUTCMonth is 0-based: 8 = September.)
  const startYear = now.getUTCMonth() >= 8 ? year + 1 : year;
  return {
    startDate: `${startYear}-09-01`,
    endDate: `${startYear + 1}-06-30`,
  };
}

const RolloverWizard: React.FC<{ locationId: string; onClose: () => void }> = ({
  locationId,
  onClose,
}) => {
  const [runPreview, { isLoading: previewing }] = useRolloverPreviewMutation();
  const [runApply, { isLoading: applying }] = useRolloverApplyMutation();
  const [runUndo, { isLoading: undoing }] = useRolloverUndoMutation();

  const [step, setStep] = useState<Step>('PREVIEW');
  const initial = useMemo(defaultRange, []);
  const [range, setRange] = useState(initial);

  // Per-class date overrides, keyed by classId. Absent → use the range default.
  const [classDates, setClassDates] = useState<
    Record<string, { startDate: string; endDate: string }>
  >({});
  // Explicit leavers, keyed by classId → set of studentIds NOT returning.
  const [leavers, setLeavers] = useState<Record<string, Set<string>>>({});
  // "Keep this class next year even though it has nobody continuing?" per classId.
  const [keepEmpty, setKeepEmpty] = useState<Record<string, boolean>>({});

  const [preview, setPreview] = useState<RolloverClassPreview[] | null>(null);
  const [result, setResult] = useState<RolloverApplyResult | null>(null);
  const [error, setError] = useState('');
  const [undoBlockers, setUndoBlockers] = useState<UndoBlocker[]>([]);
  const [undone, setUndone] = useState(false);

  // Only classes with a covering term participate — others have nothing to roll.
  const rollableClasses = useMemo(() => (preview ?? []).filter((c) => c.currentTermId), [preview]);

  /** Assemble the per-class body from the current date + leaver selections. */
  const buildPerClass = (): RolloverPerClassInput[] =>
    rollableClasses.map((c) => {
      const dates = classDates[c.classId];
      const leaveSet = leavers[c.classId];
      return {
        classId: c.classId,
        ...(dates?.startDate ? { startDate: dates.startDate } : {}),
        ...(dates?.endDate ? { endDate: dates.endDate } : {}),
        ...(leaveSet && leaveSet.size > 0 ? { leaveStudentIds: Array.from(leaveSet) } : {}),
        keepTermIfEmpty: keepEmpty[c.classId] === true ? true : undefined,
      };
    });

  const loadPreview = async () => {
    setError('');
    try {
      const data = await runPreview({
        locationId,
        nextTermRange: range,
        perClass: buildPerClass(),
      }).unwrap();
      setPreview(data);
      setStep('LEAVE');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const toggleLeaver = (classId: string, studentId: string) => {
    setLeavers((prev) => {
      const next = { ...prev };
      const set = new Set(next[classId] ?? []);
      if (set.has(studentId)) set.delete(studentId);
      else set.add(studentId);
      next[classId] = set;
      return next;
    });
  };

  // Bulk leaver update, scoped to exactly the studentIds passed in (the card
  // filters these — e.g. by search — so this must never add ids on its own).
  const setLeaving = (classId: string, ids: string[], leaving: boolean) => {
    setLeavers((prev) => {
      const next = { ...prev };
      const set = new Set(next[classId] ?? []);
      ids.forEach((id) => (leaving ? set.add(id) : set.delete(id)));
      next[classId] = set;
      return next;
    });
  };
  const onBulk = (classId: string, mode: 'leaving' | 'continuing', ids: string[]) =>
    setLeaving(classId, ids, mode === 'leaving');

  // Summary counts for the confirm step: continuing = preview.continuing
  // minus this session's ticked leavers.
  const summary = useMemo(() => {
    let continuing = 0;
    let leaverCount = 0;
    rollableClasses.forEach((c) => {
      const leaveSet = leavers[c.classId] ?? new Set<string>();
      c.continuing.forEach((ch) => {
        if (leaveSet.has(ch.studentId)) leaverCount += 1;
        else continuing += 1;
      });
    });
    return { continuing, leavers: leaverCount };
  }, [rollableClasses, leavers]);

  const apply = async () => {
    setError('');
    try {
      const data = await runApply({
        locationId,
        nextTermRange: range,
        perClass: buildPerClass(),
        confirm: true,
      }).unwrap();
      setResult(data);
      setStep('RESULT');
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const undo = async () => {
    if (!result) return;
    setError('');
    setUndoBlockers([]);
    try {
      await runUndo({ locationId, rolloverId: result.rolloverId }).unwrap();
      setUndone(true);
    } catch (err: any) {
      const data = err?.data;
      if (Array.isArray(data?.blockers)) {
        setUndoBlockers(data.blockers as UndoBlocker[]);
        return;
      }
      setError(getErrorMessage(err));
    }
  };

  const classDate = (classId: string, field: 'startDate' | 'endDate', fallback: string) =>
    classDates[classId]?.[field] ?? fallback;

  const setClassDate = (classId: string, field: 'startDate' | 'endDate', value: string) =>
    setClassDates((prev) => ({
      ...prev,
      [classId]: {
        startDate: field === 'startDate' ? value : prev[classId]?.startDate ?? '',
        endDate: field === 'endDate' ? value : prev[classId]?.endDate ?? '',
      },
    }));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="rollover-wizard">
      <div className="flex items-start justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-3xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Year rollover</h3>
            <button
              type="button"
              data-testid="rollover-close"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {error && (
            <div
              className="rounded-md bg-red-50 border border-red-200 p-3 mb-4"
              data-testid="rollover-error"
            >
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* STEP 1: PREVIEW input — the single next-school-year range. */}
          {step === 'PREVIEW' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Set the next school year. Every class's next term defaults to this range; you can
                adjust individual classes on the next step.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Next year starts
                  </label>
                  <input
                    type="date"
                    data-testid="rollover-next-year-start"
                    value={range.startDate}
                    onChange={(e) => setRange((r) => ({ ...r, startDate: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Next year ends</label>
                  <input
                    type="date"
                    data-testid="rollover-next-year-end"
                    value={range.endDate}
                    onChange={(e) => setRange((r) => ({ ...r, endDate: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  data-testid="rollover-step-next"
                  disabled={previewing || !range.startDate || !range.endDate}
                  onClick={loadPreview}
                  className="px-4 py-2 border border-transparent rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                >
                  {previewing ? 'Loading preview…' : 'Preview rollover'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: CONTINUE / LEAVE — per-class rows with editable dates,
              age-out hints, and a per-child leaver tick. Capacity conflicts
              are surfaced later, at the CONFIRM step. */}
          {step === 'LEAVE' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Everyone continues by default. Tick any child who is <strong>not</strong> returning
                next year. Children flagged as over the age limit are suggested but not pre-selected
                — confirm each one.
              </p>

              {rollableClasses.length === 0 && (
                <p className="text-sm text-gray-500" data-testid="rollover-nothing-to-roll">
                  No classes have a current term to roll forward.
                </p>
              )}

              <div className="space-y-3">
                {rollableClasses.map((c) => (
                  <RolloverClassCard
                    key={c.classId}
                    cls={c}
                    leaveSet={leavers[c.classId] ?? new Set()}
                    onToggleLeaver={(sid) => toggleLeaver(c.classId, sid)}
                    onBulk={(mode, ids) => onBulk(c.classId, mode, ids)}
                    keepEmpty={keepEmpty[c.classId] === true}
                    onKeepEmptyChange={(keep) => setKeepEmpty((p) => ({ ...p, [c.classId]: keep }))}
                    startDate={classDate(c.classId, 'startDate', c.proposedTerm.startDate)}
                    endDate={classDate(c.classId, 'endDate', c.proposedTerm.endDate)}
                    onDateChange={(field, value) => setClassDate(c.classId, field, value)}
                  />
                ))}
              </div>

              <div
                data-testid="rollover-step2-footer"
                className="sticky bottom-0 mt-4 flex items-center justify-between rounded-lg border bg-gray-50 px-4 py-3"
              >
                <span className="text-sm text-gray-600">
                  <b>{summary.continuing}</b> continuing · <b>{summary.leavers}</b> leaving ·{' '}
                  <b>{rollableClasses.length}</b> classes
                </span>
                <span className="flex gap-3">
                  <button
                    type="button"
                    data-testid="rollover-step2-back"
                    onClick={() => setStep('PREVIEW')}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    data-testid="rollover-step-next-confirm"
                    disabled={rollableClasses.length === 0}
                    onClick={() => setStep('CONFIRM')}
                    className="px-4 py-2 border border-transparent rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  >
                    Review &amp; confirm →
                  </button>
                </span>
              </div>
            </div>
          )}

          {/* STEP 3: CONFIRM — exactly what will happen, then Apply. */}
          {step === 'CONFIRM' &&
            (() => {
              const confirmRows = rollableClasses.map((c) => {
                const leaveSet = leavers[c.classId] ?? new Set<string>();
                const continuing = c.continuing.filter((ch) => !leaveSet.has(ch.studentId)).length;
                const leaving = c.continuing.filter((ch) => leaveSet.has(ch.studentId)).length;
                const kept = keepEmpty[c.classId] === true;
                // Mirrors the backend's `active` filter in applyRollover
                // (src/rollover/utils/rollover.ts): a class with no continuing
                // AND no leaving children never gets a term, no matter what
                // keepEmpty says — it can only ever be 'Ending'.
                const hasChildren = c.continuing.length > 0 || c.leavers.length > 0;
                const status: 'Continuing' | 'Ending' | 'Empty term kept' =
                  continuing > 0
                    ? 'Continuing'
                    : kept && hasChildren
                    ? 'Empty term kept'
                    : 'Ending';
                return {
                  classId: c.classId,
                  className: c.className,
                  startDate: classDate(c.classId, 'startDate', c.proposedTerm.startDate),
                  endDate: classDate(c.classId, 'endDate', c.proposedTerm.endDate),
                  continuing,
                  leaving,
                  status,
                  overCapacity: c.capacityConflicts.length > 0,
                };
              });
              const termsToCreate = confirmRows.filter((r) => r.status !== 'Ending').length;

              return (
                <div className="space-y-4" data-testid="rollover-confirm">
                  <p className="text-sm text-gray-500">
                    Review before applying. This creates terms and enrolments across the location.
                  </p>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500">
                          <th className="pb-1 pr-2">Class</th>
                          <th className="pb-1 pr-2">Next term</th>
                          <th className="pb-1 pr-2">Continuing</th>
                          <th className="pb-1 pr-2">Leaving</th>
                          <th className="pb-1 pr-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {confirmRows.map((r) => (
                          <tr
                            key={r.classId}
                            data-testid={`rollover-confirm-row-${r.classId}`}
                            className="border-t"
                          >
                            <td className="py-1 pr-2">
                              <div className="flex items-center gap-2">
                                <span>{r.className}</span>
                                {r.overCapacity && (
                                  <span
                                    data-testid={`rollover-confirm-capacity-${r.classId}`}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                                  >
                                    Over capacity
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-1 pr-2">
                              {r.status === 'Ending' ? '—' : `${r.startDate} → ${r.endDate}`}
                            </td>
                            <td className="py-1 pr-2">{r.continuing}</td>
                            <td className="py-1 pr-2">{r.leaving}</td>
                            <td className="py-1 pr-2">
                              <span data-testid={`rollover-confirm-status-${r.classId}`}>
                                {r.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <dl className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <dt className="text-xs text-gray-500">Terms to create</dt>
                      <dd
                        className="text-2xl font-semibold text-gray-900"
                        data-testid="rollover-summary-terms"
                      >
                        {termsToCreate}
                      </dd>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <dt className="text-xs text-gray-500">Children continuing</dt>
                      <dd
                        className="text-2xl font-semibold text-gray-900"
                        data-testid="rollover-summary-continuing"
                      >
                        {summary.continuing}
                      </dd>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <dt className="text-xs text-gray-500">Leavers scheduled</dt>
                      <dd
                        className="text-2xl font-semibold text-gray-900"
                        data-testid="rollover-summary-leavers"
                      >
                        {summary.leavers}
                      </dd>
                    </div>
                  </dl>

                  <div className="flex justify-between gap-3 pt-2">
                    <button
                      type="button"
                      data-testid="rollover-step-back"
                      onClick={() => setStep('LEAVE')}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      data-testid="rollover-confirm-apply"
                      disabled={applying}
                      onClick={apply}
                      className="px-4 py-2 border border-transparent rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {applying ? 'Applying…' : 'Apply rollover'}
                    </button>
                  </div>
                </div>
              );
            })()}

          {/* STEP 4 + 5: RESULT + UNDO. */}
          {step === 'RESULT' && result && (
            <div className="space-y-4" data-testid="rollover-result">
              <div className="rounded-md bg-green-50 border border-green-200 p-3">
                <p className="text-sm text-green-800">
                  Rollover applied: {result.createdTerms.length} term
                  {result.createdTerms.length === 1 ? '' : 's'} created,{' '}
                  <span data-testid="rollover-result-enrolled">{result.enrolledCount}</span>{' '}
                  children enrolled, {result.leaversScheduled.length} leaver
                  {result.leaversScheduled.length === 1 ? '' : 's'} scheduled.
                </p>
              </div>

              {result.createdTerms.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Terms created</h4>
                  <ul className="text-sm text-gray-700 list-disc list-inside">
                    {result.createdTerms.map((t) => (
                      <li key={t.termId} data-testid={`rollover-created-term-${t.classId}`}>
                        {t.className}: {t.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CRUCIAL: skipped-over-capacity children, prominent and named. */}
              {result.skippedOverCapacity.length > 0 && (
                <div
                  className="rounded-md bg-amber-50 border border-amber-300 p-3"
                  data-testid="rollover-skipped"
                >
                  <p className="text-sm font-medium text-amber-900">
                    {result.skippedOverCapacity.length} child
                    {result.skippedOverCapacity.length === 1 ? '' : 'ren'} could not be enrolled —
                    over capacity:
                  </p>
                  <ul className="mt-2 text-sm text-amber-900 list-disc list-inside">
                    {result.skippedOverCapacity.map((s) => (
                      <li key={s.studentId} data-testid={`rollover-skipped-${s.studentId}`}>
                        {s.name} ({s.className})
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.leaversScheduled.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Leavers scheduled</h4>
                  <ul className="text-sm text-gray-700 list-disc list-inside">
                    {result.leaversScheduled.map((l) => (
                      <li
                        key={l.studentId}
                        data-testid={`rollover-leaver-scheduled-${l.studentId}`}
                      >
                        {l.name} — last day {l.expectedEndDate}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* UNDO affordance. */}
              {undone ? (
                <div
                  className="rounded-md bg-blue-50 border border-blue-200 p-3"
                  data-testid="rollover-undo-success"
                >
                  <p className="text-sm text-blue-800">
                    This rollover was undone. The created terms and enrolments have been removed.
                  </p>
                </div>
              ) : (
                <>
                  {undoBlockers.length > 0 && (
                    <div
                      className="rounded-md bg-red-50 border border-red-200 p-3"
                      data-testid="rollover-undo-blockers"
                    >
                      <p className="text-sm font-medium text-red-800">
                        This rollover can no longer be fully undone:
                      </p>
                      <ul className="mt-2 space-y-1 text-sm text-red-800">
                        {undoBlockers.map((b) => (
                          <li key={b.termId} data-testid={`rollover-undo-blocker-${b.termId}`}>
                            {b.reason === 'OPERATIONAL_DATA'
                              ? `${b.termName} has attendance or sessions recorded — it cannot be undone.`
                              : `${b.termName} has ${b.manualEnrollments ?? 0} child${
                                  (b.manualEnrollments ?? 0) === 1 ? '' : 'ren'
                                } enrolled after the rollover — remove them first.`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex justify-between gap-3 pt-2">
                    <button
                      type="button"
                      data-testid="rollover-done"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      data-testid="rollover-undo"
                      disabled={undoing}
                      onClick={undo}
                      className="px-4 py-2 border border-transparent rounded-md text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                      {undoing ? 'Undoing…' : 'Undo this rollover'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolloverWizard;
